/**
 * studioProject.service.js
 *
 * Manages SDLC Studio project / conversation state.
 *
 * Storage model:
 *   1) Local cache in localStorage for instant UI responsiveness
 *   2) Best-effort sync to backend DynamoDB via /studio/projects endpoints
 */

import { lambdaClient } from '../config/http.client';

const STORAGE_KEY = 'ias_studio_projects_v1';
const LAST_KEY = 'ias_studio_last_project';
const FENCE_REGEX = /```(?:json)?/gi;
const MSG_REGEX = /"message"\s*:\s*"((?:\\.|[^"\\])*)"/s;
const AGENT_FALLBACK =
  'I hit a temporary response formatting issue. Please retry your last message.';

// Topics catalog
export const ALL_TOPICS = [
  'system_type',
  'target_users',
  'scale',
  'end_goal',
  'functional_requirements',
  'non_functional',
  'definition_of_done',
  'security_compliance',
];

export const TOPIC_LABELS = {
  system_type: 'System Type',
  target_users: 'Target Users / Segment',
  scale: 'Scale Expectations',
  end_goal: 'End Goal & Success Metrics',
  functional_requirements: 'Functional Requirements',
  non_functional: 'Non-Functional Requirements',
  definition_of_done: 'Definition of Done (DoD)',
  security_compliance: 'Security & Compliance',
};

const REQUIREMENT_MANDATORY_TOPICS = [...ALL_TOPICS];

export const EMPTY_GATHERED = Object.fromEntries(ALL_TOPICS.map((k) => [k, null]));

export const EMPTY_PHASE_CONTEXT = {
  current_phase: 'requirements',
  design_gathered: {},
  documentation_gathered: {},
  finalized_documents: {},
  captured_details: {
    requirements: [],
    design: [],
    documentation: [],
  },
  pending_conflicts: {
    requirements: {},
    design: {},
    documentation: {},
  },
};

const EMPTY_PHASE_MESSAGES = {
  requirements: [],
  design: [],
  documentation: [],
};

export const PHASE_KEYS = ['requirements', 'design', 'documentation'];

// Primitive helpers
const uid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);

const extractFirstJsonObject = (text) => {
  if (typeof text !== 'string') return null;
  const src = text.trim();
  if (!src) return null;

  for (let i = 0; i < src.length; i += 1) {
    if (src[i] !== '{') continue;

    let depth = 0;
    let inString = false;
    let escape = false;

    for (let j = i; j < src.length; j += 1) {
      const ch = src[j];

      if (inString) {
        if (escape) {
          escape = false;
          continue;
        }
        if (ch === '\\') {
          escape = true;
          continue;
        }
        if (ch === '"') {
          inString = false;
        }
        continue;
      }

      if (ch === '"') {
        inString = true;
        continue;
      }
      if (ch === '{') {
        depth += 1;
        continue;
      }
      if (ch === '}') {
        depth -= 1;
        if (depth === 0) {
          const candidate = src.slice(i, j + 1);
          try {
            return JSON.parse(candidate);
          } catch {
            break;
          }
        }
      }
    }
  }

  return null;
};

const cleanAgentContent = (value) => {
  if (typeof value !== 'string') return AGENT_FALLBACK;
  const trimmed = value.trim();
  if (!trimmed) return AGENT_FALLBACK;

  const unfenced = trimmed.replace(FENCE_REGEX, '').trim();
  const parsed = extractFirstJsonObject(trimmed) || extractFirstJsonObject(unfenced);
  if (parsed && typeof parsed.message === 'string' && parsed.message.trim()) {
    return parsed.message.trim();
  }

  const match = trimmed.match(MSG_REGEX) || unfenced.match(MSG_REGEX);
  if (match?.[1]) {
    try {
      const decoded = JSON.parse(`"${match[1]}"`);
      if (typeof decoded === 'string' && decoded.trim()) return decoded.trim();
    } catch {
      const decoded = match[1].replace(/\\n/g, '\n').replace(/\\"/g, '"').trim();
      if (decoded) return decoded;
    }
  }

  if (['', '{', '}', 'json'].includes(unfenced.toLowerCase())) {
    return AGENT_FALLBACK;
  }

  if (unfenced.startsWith('{') || unfenced.startsWith('[')) {
    return AGENT_FALLBACK;
  }

  return unfenced;
};

const normalizeRequestedPhase = (phase) => {
  if (PHASE_KEYS.includes(phase)) return phase;
  if (phase === 'completed') return 'documentation';
  return 'requirements';
};

const normalizePhaseContext = (ctx = {}) => {
  const raw = ctx && typeof ctx === 'object' ? ctx : {};
  const capturedRaw = raw.captured_details && typeof raw.captured_details === 'object'
    ? raw.captured_details
    : {};

  return {
    ...EMPTY_PHASE_CONTEXT,
    ...raw,
    finalized_documents: {
      ...EMPTY_PHASE_CONTEXT.finalized_documents,
      ...(raw.finalized_documents || {}),
    },
    pending_conflicts: {
      ...EMPTY_PHASE_CONTEXT.pending_conflicts,
      ...(raw.pending_conflicts || {}),
    },
    captured_details: {
      requirements: Array.isArray(capturedRaw.requirements) ? capturedRaw.requirements.filter(Boolean) : [],
      design: Array.isArray(capturedRaw.design) ? capturedRaw.design.filter(Boolean) : [],
      documentation: Array.isArray(capturedRaw.documentation) ? capturedRaw.documentation.filter(Boolean) : [],
    },
  };
};

const normalizePhaseMessages = (project = {}, normalizedMessages = []) => {
  const raw = project.phaseMessages && typeof project.phaseMessages === 'object'
    ? project.phaseMessages
    : {};

  const phaseMessages = {
    requirements: Array.isArray(raw.requirements) ? raw.requirements : [],
    design: Array.isArray(raw.design) ? raw.design : [],
    documentation: Array.isArray(raw.documentation) ? raw.documentation : [],
  };

  // Legacy projects only had a shared timeline.
  if (!phaseMessages.requirements.length && Array.isArray(normalizedMessages) && normalizedMessages.length) {
    phaseMessages.requirements = [...normalizedMessages];
  }

  return phaseMessages;
};

const normalizeProject = (project) => {
  if (!project || typeof project !== 'object') return project;

  const messages = Array.isArray(project.messages)
    ? project.messages.map((m) => {
        if (!m || typeof m !== 'object') return m;
        if (m.role !== 'agent') return m;
        return { ...m, content: cleanAgentContent(m.content) };
      })
    : [];

  const phaseContext = normalizePhaseContext(project.phaseContext);
  const phaseHistory = project.phaseHistory && typeof project.phaseHistory === 'object'
    ? project.phaseHistory
    : {};
  const phaseMessages = normalizePhaseMessages(project, messages);

  return { ...project, messages, phaseContext, phaseHistory, phaseMessages };
};

const sortNewest = (arr = []) =>
  [...arr].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));

const logSyncError = (op, err) => {
  console.warn(`[StudioProject] ${op} sync failed:`, err?.response?.data?.error || err?.message || err);
};

// CRUD local cache
const _read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(normalizeProject) : [];
  } catch {
    return [];
  }
};

const _write = (projects) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
};

export const getProjects = () => sortNewest(_read());

export const getProject = (id) => _read().find((p) => p.id === id) || null;

const _createProjectOnServer = async (project) => {
  try {
    await lambdaClient.post('/studio/projects', project);
  } catch (err) {
    logSyncError('create', err);
  }
};

const _upsertProjectOnServer = async (project) => {
  if (!project?.id) return;
  try {
    await lambdaClient.put(`/studio/projects/${encodeURIComponent(project.id)}`, project);
  } catch (err) {
    logSyncError('update', err);
  }
};

const _deleteProjectOnServer = async (projectId) => {
  if (!projectId) return;
  try {
    await lambdaClient.delete(`/studio/projects/${encodeURIComponent(projectId)}`);
  } catch (err) {
    logSyncError('delete', err);
  }
};

let _hydratePromise = null;

export const hydrateProjectsFromDdb = async () => {
  if (_hydratePromise) return _hydratePromise;

  _hydratePromise = (async () => {
    const local = sortNewest(_read());
    try {
      const res = await lambdaClient.get('/studio/projects');
      const remoteRaw = res?.data?.projects || [];
      const remoteNormalized = remoteRaw.map(normalizeProject);
      const remote = sortNewest(remoteNormalized);

      if (remote.length > 0) {
        _write(remote);

        const changed = JSON.stringify(remoteRaw) !== JSON.stringify(remoteNormalized);
        if (changed) {
          Promise.allSettled(remoteNormalized.map((p) => _upsertProjectOnServer(p)));
        }

        return remote;
      }

      if (local.length > 0) {
        await Promise.allSettled(local.map((p) => _upsertProjectOnServer(p)));
      }
      return local;
    } catch (err) {
      logSyncError('hydrate', err);
      return local;
    } finally {
      _hydratePromise = null;
    }
  })();

  return _hydratePromise;
};

export const createProject = (name = 'New Project') => {
  const now = new Date().toISOString();
  const project = {
    id: uid(),
    name,
    createdAt: now,
    updatedAt: now,
    messages: [],
    phaseMessages: { ...EMPTY_PHASE_MESSAGES },
    gathered: { ...EMPTY_GATHERED },
    topicsRemaining: [...ALL_TOPICS],
    isComplete: false,
    analysisResult: null,
    phaseContext: { ...EMPTY_PHASE_CONTEXT },
    phaseHistory: {},
    selectedModel: 'us.meta.llama3-3-70b-instruct-v1:0',
    suggestedName: null,
    _userRenamed: false,
  };

  const all = _read();
  all.unshift(project);
  _write(all);
  setLastProjectId(project.id);

  _createProjectOnServer(project);
  return project;
};

export const updateProject = (id, updates) => {
  const all = _read();
  const idx = all.findIndex((p) => p.id === id);
  if (idx === -1) return null;

  all[idx] = { ...all[idx], ...updates, updatedAt: new Date().toISOString() };
  _write(all);
  _upsertProjectOnServer(all[idx]);
  return all[idx];
};

export const getActivePhase = (project = {}) =>
  project?.phaseContext?.current_phase || 'requirements';

export const addMessage = (projectId, message) => {
  const msg = {
    id: uid(),
    ts: new Date().toISOString(),
    ...message,
    ...(message?.role === 'agent' ? { content: cleanAgentContent(message.content) } : {}),
  };

  const project = getProject(projectId);
  if (!project) return null;

  const phase = normalizeRequestedPhase(message?.phase || getActivePhase(project));
  const phaseMessages = normalizePhaseMessages(project, project.messages || []);
  phaseMessages[phase] = [...phaseMessages[phase], msg];

  return updateProject(projectId, {
    messages: [...(project.messages || []), msg],
    phaseMessages,
  });
};

export const deleteProject = (id) => {
  _write(_read().filter((p) => p.id !== id));
  _deleteProjectOnServer(id);
};

export const renameProject = (id, name) =>
  updateProject(id, { name, _userRenamed: true });

export const setLastProjectId = (id) =>
  sessionStorage.setItem(LAST_KEY, id || '');

export const getLastProjectId = () =>
  sessionStorage.getItem(LAST_KEY) || null;

export const countFilled = (gathered = {}) =>
  ALL_TOPICS.filter((k) => gathered[k] && gathered[k] !== 'skipped').length;

const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

export const canAnalyse = (gathered = {}) =>
  REQUIREMENT_MANDATORY_TOPICS.every((k) => hasValue(gathered[k]));

export const canAnalyseByPhase = (project = {}) => {
  const phase = getActivePhase(project);
  if (phase === 'requirements') return canAnalyse(project?.gathered || {});

  if (phase === 'design') {
    const d = project?.phaseContext?.design_gathered || {};
    return Boolean(d.architecture_style || d.high_level_components || d.api_contracts || d.db_schema);
  }

  if (phase === 'documentation') {
    const d = project?.phaseContext?.documentation_gathered || {};
    return Boolean(d.api_documentation || d.frontend_documentation || d.backend_documentation || d.architecture_documentation);
  }

  return false;
};

export const getMessagesForPhase = (project = {}, phase = 'requirements') => {
  const key = normalizeRequestedPhase(phase);
  const phaseMessages = project?.phaseMessages || {};
  const messages = phaseMessages[key];
  const hasStructuredPhaseBuckets = Object.values(phaseMessages).some(Array.isArray);

  if (Array.isArray(messages)) {
    return messages;
  }

  // Backward-compatibility fallback for legacy projects that predate phase buckets.
  if (!hasStructuredPhaseBuckets && key === 'requirements' && Array.isArray(project?.messages)) {
    return project.messages;
  }

  return [];
};
